// Everything Auto Mod needs to read a submitted photo: where it says it was
// taken, how much to trust that point, and which spots sit around it.
import haversine from 'haversine-distance';

export const MAX_SPOT_RADIUS_METERS = 500;

const DEFAULT_SPATIAL_CELL_SIZE_DEGREES = 0.01;
const METERS_PER_LATITUDE_DEGREE = 111320;
const DISTANCE_EPSILON_METERS = 0.000001;
const USER_PHOTO_BASE_URL = 'https://statics.getnofilter.com/photos/regular/';
const STATIC_BASE_URL = 'https://statics.getnofilter.com/';
const FIREBASE_STORAGE_BASE_URL = 'https://storage.googleapis.com/mari-a5cc7.appspot.com/';

const asFiniteNumber = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const dmsToDecimal = value => {
  if (!Array.isArray(value)) {
    return asFiniteNumber(value);
  }

  const degrees = asFiniteNumber(value[0]);
  const minutes = asFiniteNumber(value[1]) || 0;
  const seconds = asFiniteNumber(value[2]) || 0;

  if (degrees === null) {
    return null;
  }

  const sign = degrees < 0 ? -1 : 1;
  return sign * (Math.abs(degrees) + minutes / 60 + seconds / 3600);
};

const isValidPosition = position =>
  Boolean(
    position &&
      Number.isFinite(position.lat) &&
      Number.isFinite(position.lng) &&
      position.lat >= -90 &&
      position.lat <= 90 &&
      position.lng >= -180 &&
      position.lng <= 180
  );

const positionFromValues = (rawLat, rawLng) => {
  const lat = asFiniteNumber(rawLat);
  const lng = asFiniteNumber(rawLng);
  const position = { lat, lng };

  return isValidPosition(position) ? position : null;
};

const positionFromExif = exif => {
  if (!exif || typeof exif !== 'object') {
    return null;
  }

  const rawLat = dmsToDecimal(exif.GPSLatitude ?? exif.Latitude);
  const rawLng = dmsToDecimal(exif.GPSLongitude ?? exif.Longitude);

  if (rawLat === null || rawLng === null) {
    return null;
  }

  const latRef = String(exif.GPSLatitudeRef ?? exif.LatitudeRef ?? '').toUpperCase();
  const lngRef = String(exif.GPSLongitudeRef ?? exif.LongitudeRef ?? '').toUpperCase();
  const position = {
    lat: latRef === 'S' ? -Math.abs(rawLat) : rawLat,
    lng: lngRef === 'W' ? -Math.abs(rawLng) : rawLng,
  };

  return isValidPosition(position) ? position : null;
};

export const getPhotoPosition = photo => {
  if (!photo || typeof photo !== 'object') {
    return null;
  }

  const location = photo.location || {};
  const position = photo.position || location.position || {};

  return (
    positionFromValues(photo.lat, photo.lng) ||
    positionFromValues(photo.latitude, photo.longitude) ||
    positionFromValues(location.lat, location.lng) ||
    positionFromValues(position.lat ?? position.latitude, position.lng ?? position.longitude)
  );
};

const normalizePhotoUrl = rawUrl => {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return null;
  }

  return rawUrl
    .trim()
    .replace(FIREBASE_STORAGE_BASE_URL, STATIC_BASE_URL)
    .replace(/\.jpg(?=($|[?#]))/i, '.webp');
};

export const getPhotoUrl = photo => {
  if (!photo) {
    return null;
  }

  if (typeof photo === 'string') {
    if (/^https?:\/\//i.test(photo)) {
      return normalizePhotoUrl(photo);
    }

    const path = photo.replace(/^\/+/, '');
    return normalizePhotoUrl(
      path.startsWith('photos/') ? `${STATIC_BASE_URL}${path}` : `${USER_PHOTO_BASE_URL}${path}`
    );
  }

  const directUrl =
    photo.imageUrl || photo.url || (photo.urls && (photo.urls.regular || photo.urls.full));

  if (directUrl) {
    return normalizePhotoUrl(directUrl);
  }

  return photo.file ? getPhotoUrl(photo.file) : null;
};

export const getOriginalGps = photo => {
  if (!photo || typeof photo !== 'object') {
    return null;
  }

  const exif = photo.exif && typeof photo.exif === 'object' ? photo.exif : {};
  const assetData = exif.assetData && typeof exif.assetData === 'object' ? exif.assetData : {};
  const assetExif = assetData.exif && typeof assetData.exif === 'object' ? assetData.exif : {};
  const candidates = [
    {
      position: positionFromValues(
        photo.originalGps && (photo.originalGps.lat ?? photo.originalGps.latitude),
        photo.originalGps && (photo.originalGps.lng ?? photo.originalGps.longitude)
      ),
      source: (photo.originalGps && photo.originalGps.source) || 'recorded_original_gps',
    },
    {
      position: positionFromValues(
        photo.originalLocation &&
          (photo.originalLocation.lat ?? photo.originalLocation.latitude),
        photo.originalLocation &&
          (photo.originalLocation.lng ?? photo.originalLocation.longitude)
      ),
      source: 'recorded_original_location',
    },
    { position: positionFromExif(exif), source: 'exif' },
    { position: positionFromExif(exif['{GPS}']), source: 'exif_gps_dictionary' },
    {
      position: positionFromValues(
        assetData.location && (assetData.location.lat ?? assetData.location.latitude),
        assetData.location && (assetData.location.lng ?? assetData.location.longitude)
      ),
      source: 'asset_location',
    },
    { position: positionFromExif(assetExif), source: 'asset_exif' },
    {
      position: positionFromExif(assetExif['{GPS}']),
      source: 'asset_exif_gps_dictionary',
    },
  ];
  const candidate = candidates.find(item => item.position);

  return candidate ? { ...candidate.position, source: candidate.source } : null;
};

const parseNativeGpsFlag = value => {
  if (value === true || value === 'true') {
    return true;
  }

  if (value === false || value === 'false') {
    return false;
  }

  return null;
};

export const getLocationProvenance = photo => {
  const selectedGps = getPhotoPosition(photo);
  const originalGps = getOriginalGps(photo);
  const nativeGpsFlag = parseNativeGpsFlag(photo && photo.isNativeGPS);
  const correctionDistanceMeters =
    selectedGps && originalGps ? haversine(selectedGps, originalGps) : null;

  if (!selectedGps) {
    return {
      kind: 'missing',
      label: 'No submitted GPS',
      detail: 'This photo has no usable submitted coordinates.',
      tone: 'danger',
      selectedGps: null,
      originalGps,
      correctionDistanceMeters: null,
      isNative: false,
      isManual: false,
    };
  }

  if (nativeGpsFlag === true) {
    return {
      kind: 'native',
      label: 'Original camera GPS',
      detail: 'The submitted point came directly from the photo or device metadata.',
      tone: 'positive',
      selectedGps,
      originalGps: originalGps || { ...selectedGps, source: 'native_submission' },
      correctionDistanceMeters: null,
      isNative: true,
      isManual: false,
    };
  }

  if (nativeGpsFlag === false && originalGps) {
    return {
      kind: 'manual_correction',
      label: 'GPS corrected by uploader',
      detail: 'The uploader refined a camera GPS point that is still preserved for comparison.',
      tone: 'positive',
      selectedGps,
      originalGps,
      correctionDistanceMeters,
      isNative: false,
      isManual: true,
    };
  }

  if (nativeGpsFlag === false) {
    return {
      kind: 'manual_placement',
      label: 'Location placed manually',
      detail: 'No original camera GPS is available for comparison.',
      tone: 'warning',
      selectedGps,
      originalGps: null,
      correctionDistanceMeters: null,
      isNative: false,
      isManual: true,
    };
  }

  return {
    kind: 'unknown',
    label: 'Location source unknown',
    detail: originalGps
      ? 'Camera metadata exists, but the app did not record whether this point was changed.'
      : 'The app did not record how this point was chosen.',
    tone: 'neutral',
    selectedGps,
    originalGps,
    correctionDistanceMeters,
    isNative: false,
    isManual: false,
  };
};

export const normalizeUserPhoto = photo => {
  const rawPhoto = photo && typeof photo === 'object' ? photo : {};
  const position = getPhotoPosition(rawPhoto);
  const imageUrl = getPhotoUrl(rawPhoto);
  const locationProvenance = getLocationProvenance(rawPhoto);

  return {
    ...rawPhoto,
    id: rawPhoto.id == null ? '' : String(rawPhoto.id),
    type: 'USER',
    queue: 'user_photos',
    pointType: 'userPhoto',
    lat: position ? position.lat : null,
    lng: position ? position.lng : null,
    location: position ? { ...(rawPhoto.location || {}), ...position } : rawPhoto.location,
    imageUrl,
    url: imageUrl,
    aiReview: rawPhoto.aiReview || {},
    originalGps: locationProvenance.originalGps,
    locationProvenance,
    raw: rawPhoto,
  };
};

// A Detective photo is an Unsplash photo the app's users placed on a map. The
// backend has already clustered their answers into standpoints and put the
// strongest one on lat/lng, so the page can treat it like any other queue item:
// the position is the standpoint, not the Unsplash coordinates that failed to
// locate it.
const normalizeUnsplashCollectionPhoto = (photo, queue) => {
  const rawPhoto = photo && typeof photo === 'object' ? photo : {};
  const position = getPhotoPosition(rawPhoto);
  const imageUrl = normalizePhotoUrl(rawPhoto.url) || rawPhoto.url || null;
  const standpoints = Array.isArray(rawPhoto.standpoints) ? rawPhoto.standpoints : [];

  return {
    ...rawPhoto,
    id: rawPhoto.id == null ? '' : String(rawPhoto.id),
    // What /mod/spots/create and /mod/spots/assign read to decide which
    // collection the photo lives in.
    type: 'UNSPLASH',
    queue,
    pointType: 'photo',
    lat: position ? position.lat : null,
    lng: position ? position.lng : null,
    location: position ? { ...position } : null,
    imageUrl,
    url: imageUrl,
    standpoints,
    aiReview: rawPhoto.aiReview || {},
    raw: rawPhoto,
  };
};

export const normalizeDetectivePhoto = photo =>
  normalizeUnsplashCollectionPhoto(photo, 'detective');

// The same Unsplash collection, read before anybody has located the photo: it
// stands on the coordinate the camera wrote instead of on a detective's answer,
// so it has no standpoints and every other field is the same.
export const normalizeUnsplashPhoto = photo =>
  normalizeUnsplashCollectionPhoto(photo, 'unsplash');

const coordinateKeyPart = value => {
  const number = Object.is(value, -0) ? 0 : value;
  return String(number);
};

export const getExactCoordinateKey = photo => {
  const position = getPhotoPosition(photo);

  return position
    ? `${coordinateKeyPart(position.lat)},${coordinateKeyPart(position.lng)}`
    : null;
};

const normalizeLongitude = longitude => {
  const normalized = ((longitude + 180) % 360 + 360) % 360 - 180;
  return Object.is(normalized, -0) ? 0 : normalized;
};

const getSpatialCell = (position, cellSizeDegrees) => ({
  latCell: Math.floor((position.lat + 90) / cellSizeDegrees),
  lngCell: Math.floor((normalizeLongitude(position.lng) + 180) / cellSizeDegrees),
});

const getSpatialCellKey = (latCell, lngCell) => `${latCell}:${lngCell}`;

export const buildSpatialSpotIndex = (spots, options = {}) => {
  const requestedCellSize = asFiniteNumber(options.cellSizeDegrees);
  const cellSizeDegrees =
    requestedCellSize && requestedCellSize > 0
      ? requestedCellSize
      : DEFAULT_SPATIAL_CELL_SIZE_DEGREES;
  const buckets = new Map();
  const indexedSpots = [];

  (Array.isArray(spots) ? spots : []).forEach(spot => {
    const position = getPhotoPosition(spot);

    if (!position) {
      return;
    }

    const indexedSpot = { spot, position };
    const cell = getSpatialCell(position, cellSizeDegrees);
    const key = getSpatialCellKey(cell.latCell, cell.lngCell);

    if (!buckets.has(key)) {
      buckets.set(key, []);
    }

    buckets.get(key).push(indexedSpot);
    indexedSpots.push(indexedSpot);
  });

  return {
    buckets,
    spots: indexedSpots,
    cellSizeDegrees,
    longitudeCellCount: Math.ceil(360 / cellSizeDegrees),
  };
};

const getIndexedSpotsNear = (index, position, radiusMeters) => {
  const latitudeDelta = radiusMeters / METERS_PER_LATITUDE_DEGREE;
  const longitudeScale = Math.abs(Math.cos((position.lat * Math.PI) / 180));
  const longitudeDelta =
    longitudeScale < 0.000001
      ? 180
      : Math.min(180, radiusMeters / (METERS_PER_LATITUDE_DEGREE * longitudeScale));
  // The extra cell protects the exact-distance filter from ellipsoid/degree
  // approximations at a bucket edge. It only adds a small number of candidates.
  const latCellMin =
    Math.floor((Math.max(-90, position.lat - latitudeDelta) + 90) / index.cellSizeDegrees) - 1;
  const latCellMax =
    Math.floor((Math.min(90, position.lat + latitudeDelta) + 90) / index.cellSizeDegrees) + 1;
  const longitudeCellSpan = Math.ceil(longitudeDelta / index.cellSizeDegrees) + 1;

  if (longitudeCellSpan * 2 + 1 >= index.longitudeCellCount) {
    return index.spots;
  }

  const centerCell = getSpatialCell(position, index.cellSizeDegrees);
  const matches = new Set();

  for (let latCell = latCellMin; latCell <= latCellMax; latCell += 1) {
    for (let offset = -longitudeCellSpan; offset <= longitudeCellSpan; offset += 1) {
      const lngCell =
        ((centerCell.lngCell + offset) % index.longitudeCellCount + index.longitudeCellCount) %
        index.longitudeCellCount;
      const bucket = index.buckets.get(getSpatialCellKey(latCell, lngCell));

      if (bucket) {
        bucket.forEach(indexedSpot => matches.add(indexedSpot));
      }
    }
  }

  return Array.from(matches);
};

export const getSpotCandidates = (
  spotIndex,
  photoOrPosition,
  radiusMeters = 500,
  // Auto Mod reaches further than /moderation does, so the ceiling is a
  // parameter. Left alone it keeps the radius every other caller expects.
  maxRadiusMeters = MAX_SPOT_RADIUS_METERS
) => {
  const position = getPhotoPosition(photoOrPosition);
  const index = Array.isArray(spotIndex) ? buildSpatialSpotIndex(spotIndex) : spotIndex;
  const requestedRadius = asFiniteNumber(radiusMeters);
  const radius = Math.min(
    maxRadiusMeters,
    Math.max(0, requestedRadius === null ? maxRadiusMeters : requestedRadius)
  );

  if (!position || !index || !index.buckets || !Array.isArray(index.spots)) {
    return [];
  }

  return getIndexedSpotsNear(index, position, radius)
    .map(({ spot, position: spotPosition }) => ({
      ...spot,
      distanceMeters: haversine(position, spotPosition),
    }))
    .filter(candidate => candidate.distanceMeters <= radius + DISTANCE_EPSILON_METERS)
    .sort((a, b) => {
      const distanceDifference = a.distanceMeters - b.distanceMeters;

      if (Math.abs(distanceDifference) > DISTANCE_EPSILON_METERS) {
        return distanceDifference;
      }

      return String(a.id || '').localeCompare(String(b.id || ''));
    });
};

// A gallery tile is a couple of hundred pixels: the 500px derivative, and
// Unsplash's own small size, are all it can show.
const getSmallPhotoUrl = photo => {
  const url =
    photo && photo.urls && photo.urls.small ? normalizePhotoUrl(photo.urls.small) : getPhotoUrl(photo);

  return url && url.replace(USER_PHOTO_BASE_URL, `${STATIC_BASE_URL}photos/small/`);
};

export const getSpotGalleryUrls = spot => {
  if (!spot || typeof spot !== 'object') {
    return [];
  }

  const photos = [
    spot.mainPhotoHydrated,
    spot.mainPhoto,
    ...(Array.isArray(spot.photosHydrated) ? spot.photosHydrated : []),
    ...(Array.isArray(spot.userPhotosHydrated) ? spot.userPhotosHydrated : []),
  ].filter(Boolean);
  const mainPhotoId = spot.mainPhotoId == null ? null : String(spot.mainPhotoId);

  photos.sort((a, b) => {
    const aIsMain = mainPhotoId && String(a.id) === mainPhotoId;
    const bIsMain = mainPhotoId && String(b.id) === mainPhotoId;
    return Number(bIsMain) - Number(aIsMain);
  });

  const urls = [
    ...photos.map(getSmallPhotoUrl),
    ...(Array.isArray(spot.galleryUrls) ? spot.galleryUrls.map(getSmallPhotoUrl) : []),
  ].filter(Boolean);

  return Array.from(new Set(urls));
};

export const buildCreateSpotPayload = ({ photo, draft } = {}) => {
  const safePhoto = photo && typeof photo === 'object' ? photo : {};
  const safeDraft = draft && typeof draft === 'object' ? draft : {};
  const photoId = String(safePhoto.id || '').trim();
  const photoType = String(safePhoto.type || '').toUpperCase();
  const position = getPhotoPosition(safeDraft) || getPhotoPosition(safePhoto);

  if (!photoId) {
    throw new Error('A moderation photo id is required to create a spot.');
  }

  if (!['USER', 'UNSPLASH'].includes(photoType)) {
    throw new Error('The moderation photo type must be USER or UNSPLASH.');
  }

  if (!position) {
    throw new Error('Valid spot coordinates are required.');
  }

  const payload = {
    id: null,
    name: String(safeDraft.name || '').trim(),
    from: String(safeDraft.from || '').trim(),
    slug: String(safeDraft.slug || '').trim(),
    country: String(safeDraft.country || '').trim(),
    lat: position.lat,
    lng: position.lng,
    photos: photoType === 'UNSPLASH' ? [photoId] : [],
    userPhotos: photoType === 'USER' ? [photoId] : [],
    // Always false on create, never the real value: isTop only ever arrives in an edit.
    isTop: false,
    mainPhotoId: photoId,
  };
  const category = String(safeDraft.category || '').trim();

  if (category) {
    payload.category = category;
  }

  return payload;
};

const photoModel = {
  MAX_SPOT_RADIUS_METERS,
  buildCreateSpotPayload,
  buildSpatialSpotIndex,
  getExactCoordinateKey,
  getLocationProvenance,
  getOriginalGps,
  getPhotoPosition,
  getPhotoUrl,
  getSpotCandidates,
  getSpotGalleryUrls,
  normalizeDetectivePhoto,
  normalizeUnsplashPhoto,
  normalizeUserPhoto,
};

export default photoModel;
